import { useEffect, useState, useRef, useCallback } from "react";
import MonthPanel from "./MonthPanel";
import "./MonthRangeDropdown.scss";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const currentDate = new Date();

const MonthRangeDropdown = ({ open, startDate, value, onApply, onCancel }) => {
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const [dropUp, setDropUp] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const modalRef = useRef(null);

  // Sync with parent value
  useEffect(() => {
    setFrom(value.from);
    setTo(value.to);
  }, [value]);

  // Check if the dropdown fits below; if not, flip it upward
  const checkPosition = useCallback(() => {
    if (!modalRef.current) return;
    const rect = modalRef.current.getBoundingClientRect();
    // If the dropdown overflows below the viewport, flip it to open upward
    const bottomOverflow = rect.bottom - window.innerHeight;
    if (bottomOverflow > 0) {
      setDropUp(true);
    }
    // Also check right-side overflow – shift left to keep it on screen
    const rightOverflow = rect.right - window.innerWidth;
    if (rightOverflow > 0) {
      modalRef.current.style.left = `${-rightOverflow - 8}px`;
    }
    setIsPositioned(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setDropUp(false);
      setIsPositioned(false);
      return;
    }
    // Defer to the next frame so the browser has laid out the element
    const raf = requestAnimationFrame(checkPosition);
    return () => cancelAnimationFrame(raf);
  }, [open, checkPosition]);

  if (!open) return null;

  return (
    <div
      ref={modalRef}
      className={`month-range-modal border rounded ${dropUp ? "month-range-modal--drop-up" : ""}`}
      style={{ visibility: isPositioned ? "visible" : "hidden" }}
    >
      <div className="month-panels">
        <MonthPanel
          startDate={startDate}
          year={from.year}
          selected={from}
          months={months}
          from={from}
          to={to}
          calendarType="FROM"
          onSelect={(month) => setFrom({ ...from, month })}
          onYearChange={(y) =>
            setFrom({
              ...from,
              year: y,
              month: y === startDate.year ? startDate.month : 0,
            })
          }
          className="rounded-start"
        />

        <MonthPanel
          startDate={startDate}
          year={to.year}
          selected={to}
          months={months}
          from={from}
          to={to}
          calendarType="TO"
          onSelect={(month) => setTo({ ...to, month })}
          onYearChange={(y) =>
            setTo({
              ...to,
              year: y,
              month:
                y === currentDate.getFullYear() ? currentDate.getMonth() : 11,
            })
          }
          className="rounded-end"
        />
      </div>

      {/* FOOTER */}
      <div className="month-range-footer  border-top">
        <div className="range-text">
          <span className="rounded border p-2">
            {months[from.month]} {from.year}
          </span>{" "}
          -{" "}
          <span className="rounded border p-2">
            {months[to.month]} {to.year}
          </span>
        </div>

        <div className="actions">
          <button
            className="cancle"
            onClick={() => {
              setFrom(value.from);
              setTo(value.to);
              onCancel();
            }}
          >
            Cancel
          </button>

          <button className="apply" onClick={() => onApply({ from, to })}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonthRangeDropdown;
